import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled, { keyframes } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { SP_BREAKPOINT } from "../../constants/app";
import {
  PRIMARY_DARK_GREEN,
  PRIMARY_LIGHT_RED,
  PRIMARY_RED,
  SECONDARY_RED,
} from "../../constants/color";
import { RequiredBadge } from "./RequiredBadge";
import { SelectDropdown } from "./SelectDropdown";
import { ArrowIcon } from "./icons/ArrowIcon";
import { ErrorIcon } from "./icons/input/ErrorIcon";

export type SelectOptionType = {
  id: number | string;
  name: string;
  value: any;
};

type Props = {
  name: string;
  options: SelectOptionType[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  onChange: (name: string, value: any) => void;
  clearable?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  width?: string;
  hasSearchInput?: boolean;
};

export const Select: React.FC<Props> = ({
  name,
  label,
  placeholder = "--",
  required,
  defaultValue,
  options,
  disabled,
  onChange,
  clearable = true,
  hasError,
  errorMessage,
  width = "320px",
  hasSearchInput = true,
}) => {
  const uuid = useMemo(() => {
    return uuidv4();
  }, []);

  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const [currentName, setCurrentName] = useState<string>(defaultValue ?? "");

  const formContext = useFormContext();

  const handleOpenSelection = useCallback(() => {
    if (disabled) return;

    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleChange = useCallback(
    (optionName: string, value: string) => {
      setIsOpen(false);

      if (clearable && currentName === optionName) {
        setCurrentName(placeholder ?? "");
        onChange("", "");
        try {
          if (name)
            formContext.setValue(name, undefined, { shouldValidate: true });
        } catch (err) {}
        return;
      }

      setCurrentName(optionName);
      onChange(optionName, value);
      try {
        if (name) formContext.setValue(name, value, { shouldValidate: true });
      } catch (err) {}
    },
    [currentName, formContext],
  );

  useEffect(() => {
    try {
      if (!formContext.getValues(name)) setCurrentName(placeholder ?? "");
    } catch (err) {}
  }, [options]);

  useEffect(() => {
    const subscription = formContext?.watch((value, { name: fieldName }) => {
      // Handle reset methods
      if (!fieldName) {
        const selectedOption = options.find(
          (option) => option.value === value[name],
        );
        setCurrentName(
          selectedOption ? selectedOption.name : (placeholder ?? ""),
        );
      }
    });

    return () => subscription?.unsubscribe();
  }, [formContext]);

  const closeDropdown = useCallback((e: MouseEvent) => {
    // @ts-expect-error
    const id = e.target?.id ?? "";

    if (id.includes(uuid)) return;

    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) document.body.addEventListener("click", closeDropdown);
    else document.body.removeEventListener("click", closeDropdown);

    return () => {
      document.body.removeEventListener("click", closeDropdown);
    };
  }, [isOpen]);

  const fillColor = useMemo(() => {
    if (disabled) return "#CCCCCC";

    if (hasError) return SECONDARY_RED;

    return "#333333";
  }, [disabled, hasError]);

  return (
    <StyledContainer
      id={`container-${uuid}`}
      $disabled={disabled}
      $width={width}
    >
      <StyledHeader>
        {label && <StyledLabel>{label}</StyledLabel>}
        {required && <RequiredBadge />}
      </StyledHeader>

      <StyledSelect
        id={`select-container-${uuid}`}
        onClick={handleOpenSelection}
        $disabled={disabled}
        $hasError={hasError}
      >
        <StyledPlaceholder
          id={`select-placeholder-${uuid}`}
          $isPlaceholder={currentName === "" || currentName === placeholder}
        >
          {currentName !== "" ? currentName : placeholder}
        </StyledPlaceholder>

        <StyledArrowIcon id={`select-dropdown-${uuid}`} $isOpen={isOpen}>
          <ArrowIcon uuid={uuid} fillColor={fillColor} />
        </StyledArrowIcon>
      </StyledSelect>

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      <SelectDropdown
        isOpen={(isOpen ?? false) && !disabled}
        uuid={uuid}
        options={options}
        width={width}
        currentName={currentName}
        handleChange={handleChange}
        hasSearchInput={hasSearchInput}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $disabled?: boolean; $width: string }>`
  @media (max-width: ${SP_BREAKPOINT}) {
    width: 100%;
  }

  width: ${({ $width }) => $width};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledSelect = styled.div<{
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  width: 100%;
  height: 48px;
  padding: 17px 12px 17px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #ffffff;
  outline: none;

  ${({ $disabled, $hasError }) =>
    `
      border-color: ${
        $disabled ? "#cccccc" : $hasError ? PRIMARY_RED : "#cccccc"
      };
      background-color: ${
        $disabled ? "#f2f2f2" : $hasError ? PRIMARY_LIGHT_RED : "#ffffff"
      };
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
      $hasError ? PRIMARY_RED : PRIMARY_DARK_GREEN};
  }
`;

const StyledPlaceholder = styled.div<{
  $isPlaceholder: boolean;
}>`
  font-size: 14px;
  font-weight: 400;
  line-height: 170%;
  letter-spacing: 0.56px;
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? "#999999" : "#000000")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 80%;
`;

const rotate = ($isOpen: boolean | null) => keyframes`
  from {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 0 : 180) + "deg"});
  }
  to {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 180 : 0) + "deg"});
  }
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean | null }>`
  transform: ${({ $isOpen }) =>
    $isOpen === null
      ? "rotate(0deg)"
      : $isOpen
        ? "rotate(180deg)"
        : "rotate(0deg)"};
  animation: ${({ $isOpen }) => rotate($isOpen)} 0.2s forwards;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${PRIMARY_RED};
`;
